export type Language = 'en' | 'ru' | 'uz';

export const SUPPORTED_LANGUAGES: { code: Language; label: string; flag: string }[] = [
  { code: 'en', label: 'English', flag: '🇬🇧' },
  { code: 'ru', label: 'Русский', flag: '🇷🇺' },
  { code: 'uz', label: "O'zbek", flag: '🇺🇿' },
];

const translations = {
  en: {
    // Auth
    signIn: 'Sign In',
    createAccount: 'Create Account',
    welcomeBack: 'Welcome back',
    createYourProfile: 'Create your profile',
    signInDesc: 'Sign in to access your player card and AI coach',
    signUpDesc: 'Start building your Ultimate Team card today',
    emailAddress: 'Email address',
    password: 'Password',
    fullName: 'Full name',
    confirmPassword: 'Confirm password',
    rememberMe: 'Remember me',
    forgotPassword: 'Forgot password?',
    signingIn: 'Signing in...',
    creatingAccount: 'Creating account...',
    agreeTerms: 'I agree to the Terms of Service and Privacy Policy',
    demoAccounts: 'Demo Accounts',
    clickToFill: 'Click to auto-fill',
    yourPlayerCard: 'Your Player Card.',
    yourLegacy: 'Your Legacy.',
    heroDesc: 'Build your Ultimate Team card from real metrics. Get AI-powered coaching to reach your peak rating.',
    // Profile Setup
    physicalParameters: 'Physical Parameters',
    technicalSkills: 'Technical Skills',
    positionStyle: 'Position & Playing Style',
    physicalDesc: 'Your physical attributes determine PHY, PAC and base fitness stats',
    technicalDesc: 'Rate your technical abilities — these directly compute PAC, SHO, PAS, DRI, DEF',
    positionDesc: 'Your position and style influence stat weighting and AI coach recommendations',
    firstName: 'First Name',
    lastName: 'Last Name',
    age: 'Age',
    height: 'Height',
    weight: 'Weight',
    stamina: 'Stamina / Endurance',
    staminaDesc: 'How long can you maintain peak performance?',
    physicalStrength: 'Physical Strength',
    strengthDesc: 'Upper body power, shielding, aerial duels',
    sleepHours: 'Average Sleep (hours/night)',
    sleepDesc: 'Recovery quality directly impacts PHY rating',
    nutritionRegime: 'Nutrition Regime',
    highProtein: 'High Protein',
    highProteinDesc: 'Optimized for muscle gain & recovery',
    balanced: 'Balanced',
    balancedDesc: 'Standard macros, moderate recovery',
    lowCarb: 'Low Carb',
    lowCarbDesc: 'May reduce sustained sprint endurance',
    vegan: 'Vegan/Plant-based',
    veganDesc: 'Requires careful protein supplementation',
    lowStaminaWarning: 'Low stamina detected — AI coach will flag performance drop after 60 min',
    // Slider labels
    needsWork: 'Needs Work',
    average: 'Average',
    good: 'Good',
    elite: 'Elite',
    fatiguesEarly: 'Fatigues early',
    full90: 'Full 90 mins',
    lightBuild: 'Light build',
    dominantAerial: 'Dominant aerial',
    // Position
    playingPosition: 'Playing Position',
    positionInfluence: 'Position influences stat weighting in OVR calculation',
    nationality: 'Nationality',
    nationalityDesc: 'Displayed on your player card',
    clubTeamName: 'Club / Team Name',
    clubOptional: 'Optional — shown on card badge',
    preferredPlayingStyle: 'Preferred Playing Style',
    weeklyTrainingHours: 'Weekly Training Hours',
    weeklyTrainingDesc: 'Hours per week of structured football training',
    profileSummary: 'Profile Summary',
    // Navigation
    backToLogin: 'Back to Login',
    previous: 'Previous',
    continue: 'Continue',
    generateMyCard: 'Generate My Card',
    saving: 'Saving...',
    step: 'Step',
    of: 'of',
    livePreview: 'Live Preview',
    // Dashboard
    playerDashboard: 'Player Dashboard',
    editProfile: 'Edit Profile',
    share: 'Share',
    regenerateAI: 'Regenerate AI',
    analyzing: 'Analyzing...',
    // Admin
    adminDashboard: 'Admin Dashboard',
    registeredUsers: 'Registered Users',
    sendNotification: 'Send Notification',
    notificationTitle: 'Notification Title',
    notificationMessage: 'Message',
    sendToAll: 'Send to All Users',
    sendToUser: 'Send to User',
    sending: 'Sending...',
    notificationSent: 'Notification sent!',
    noUsers: 'No users registered yet.',
    userEmail: 'Email',
    userName: 'Name',
    userRole: 'Role',
    joinedAt: 'Joined',
    position: 'Position',
    ovr: 'OVR',
    selectUser: 'Select user...',
    allUsers: 'All Users',
    // AI Coach Panel
    aiCoachAnalysis: 'AI Coach Analysis',
    poweredByGemini: 'Powered by Gemini · Personalized to your profile',
    analysisReady: 'Analysis ready',
    aiAnalyzing: 'AI Coach is analyzing your profile...',
    aiAnalyzingDesc: 'Finding hidden patterns in your stats · Gemini API',
    // AI Tabs
    tabStrengths: 'Strengths',
    tabWeaknesses: 'Weaknesses',
    tabTactics: 'Tactics',
    tabTraining: 'Training Plan',
    tabNutrition: 'Nutrition',
    tabInsights: 'AI Insights',
    // Strengths tab
    strengthsDesc: 'Your competitive advantages — areas where you outperform players at your position.',
    keyStrengths: 'Key Strengths',
    // Weaknesses tab
    weaknessesDesc: 'Areas requiring focused development — honest assessment to unlock your next rating tier.',
    priorityImprovements: 'Priority Improvements to Raise OVR',
    // Tactics tab
    tacticsDesc: 'Position-specific tactical advice tailored to your stat profile and playing style.',
    recommendedFormations: 'Recommended Formations for',
    // Training tab
    trainingDesc: '7-day training schedule personalized to your current stats and target improvements.',
    weeklyLoadSummary: 'Weekly Load Summary',
    totalTraining: 'Total Training',
    highIntensity: 'High Intensity',
    recoveryDays: 'Recovery Days',
    // Nutrition tab
    nutritionDesc: 'Nutrition protocol tailored to your diet type, body metrics, and training load.',
    dailyMacroTargets: 'Daily Macro Targets (Training Day)',
    hydrationPriority: 'Hydration Priority',
    preMatchCarbs: 'Pre-match Carbs',
    postSessionProtein: 'Post-session Protein',
    sleepOptimization: 'Sleep Optimization',
    // Insights tab
    hiddenPatternDetection: 'Hidden Pattern Detection',
    insightsDesc: 'Gemini AI found non-obvious correlations between your stats — insights a human coach might miss.',
    noPatternsTitle: 'No critical patterns detected',
    noPatternsDesc: 'Your stats are well-balanced — no hidden risks found in your current profile.',
    statCorrelationAnalysis: 'Stat Correlation Analysis',
    lateGameSprint: 'Late-game sprint effectiveness',
    pressingVsBallCarrying: 'Pressing contribution vs ball-carrying',
    recoverySpeed: 'Recovery speed between sessions',
    expectedGoals: 'Expected goals contribution per match',
    highRisk: 'high risk',
    mediumRisk: 'medium risk',
    lowRisk: 'low risk',
    // Intensity
    intensityLow: 'Low',
    intensityMedium: 'Medium',
    intensityHigh: 'High',
  },
  ru: {
    // Auth
    signIn: 'Войти',
    createAccount: 'Создать аккаунт',
    welcomeBack: 'С возвращением',
    createYourProfile: 'Создайте профиль',
    signInDesc: 'Войдите, чтобы получить доступ к карточке игрока и ИИ-тренеру',
    signUpDesc: 'Начните создавать свою карточку Ultimate Team сегодня',
    emailAddress: 'Электронная почта',
    password: 'Пароль',
    fullName: 'Полное имя',
    confirmPassword: 'Подтвердите пароль',
    rememberMe: 'Запомнить меня',
    forgotPassword: 'Забыли пароль?',
    signingIn: 'Вход...',
    creatingAccount: 'Создание аккаунта...',
    agreeTerms: 'Я согласен с Условиями использования и Политикой конфиденциальности',
    demoAccounts: 'Демо-аккаунты',
    clickToFill: 'Нажмите для автозаполнения',
    yourPlayerCard: 'Ваша карточка игрока.',
    yourLegacy: 'Ваше наследие.',
    heroDesc: 'Создайте карточку Ultimate Team на основе реальных показателей. Получите ИИ-тренировки для достижения максимального рейтинга.',
    // Profile Setup
    physicalParameters: 'Физические параметры',
    technicalSkills: 'Технические навыки',
    positionStyle: 'Позиция и стиль игры',
    physicalDesc: 'Ваши физические атрибуты определяют PHY, PAC и базовые показатели физической формы',
    technicalDesc: 'Оцените свои технические способности — они напрямую вычисляют PAC, SHO, PAS, DRI, DEF',
    positionDesc: 'Ваша позиция и стиль влияют на весовые коэффициенты статистики и рекомендации ИИ-тренера',
    firstName: 'Имя',
    lastName: 'Фамилия',
    age: 'Возраст',
    height: 'Рост',
    weight: 'Вес',
    stamina: 'Выносливость / Стамина',
    staminaDesc: 'Как долго вы можете поддерживать пиковую производительность?',
    physicalStrength: 'Физическая сила',
    strengthDesc: 'Сила верхней части тела, защита мяча, воздушные дуэли',
    sleepHours: 'Средний сон (часов/ночь)',
    sleepDesc: 'Качество восстановления напрямую влияет на рейтинг PHY',
    nutritionRegime: 'Режим питания',
    highProtein: 'Высокобелковый',
    highProteinDesc: 'Оптимизирован для набора мышечной массы и восстановления',
    balanced: 'Сбалансированный',
    balancedDesc: 'Стандартные макросы, умеренное восстановление',
    lowCarb: 'Низкоуглеводный',
    lowCarbDesc: 'Может снизить выносливость при длительных спринтах',
    vegan: 'Веган/Растительный',
    veganDesc: 'Требует тщательного подбора белковых добавок',
    lowStaminaWarning: 'Обнаружена низкая выносливость — ИИ-тренер отметит снижение производительности после 60 мин',
    // Slider labels
    needsWork: 'Нужна работа',
    average: 'Средний',
    good: 'Хорошо',
    elite: 'Элита',
    fatiguesEarly: 'Быстро устаёт',
    full90: 'Все 90 минут',
    lightBuild: 'Лёгкое телосложение',
    dominantAerial: 'Доминирует в воздухе',
    // Position
    playingPosition: 'Игровая позиция',
    positionInfluence: 'Позиция влияет на весовые коэффициенты в расчёте OVR',
    nationality: 'Национальность',
    nationalityDesc: 'Отображается на вашей карточке игрока',
    clubTeamName: 'Клуб / Команда',
    clubOptional: 'Необязательно — отображается на значке карточки',
    preferredPlayingStyle: 'Предпочтительный стиль игры',
    weeklyTrainingHours: 'Часы тренировок в неделю',
    weeklyTrainingDesc: 'Часов в неделю структурированных футбольных тренировок',
    profileSummary: 'Сводка профиля',
    // Navigation
    backToLogin: 'Назад к входу',
    previous: 'Назад',
    continue: 'Продолжить',
    generateMyCard: 'Создать мою карточку',
    saving: 'Сохранение...',
    step: 'Шаг',
    of: 'из',
    livePreview: 'Предпросмотр',
    // Dashboard
    playerDashboard: 'Панель игрока',
    editProfile: 'Редактировать профиль',
    share: 'Поделиться',
    regenerateAI: 'Обновить ИИ',
    analyzing: 'Анализ...',
    // Admin
    adminDashboard: 'Панель администратора',
    registeredUsers: 'Зарегистрированные пользователи',
    sendNotification: 'Отправить уведомление',
    notificationTitle: 'Заголовок уведомления',
    notificationMessage: 'Сообщение',
    sendToAll: 'Отправить всем пользователям',
    sendToUser: 'Отправить пользователю',
    sending: 'Отправка...',
    notificationSent: 'Уведомление отправлено!',
    noUsers: 'Пользователи ещё не зарегистрированы.',
    userEmail: 'Email',
    userName: 'Имя',
    userRole: 'Роль',
    joinedAt: 'Дата регистрации',
    position: 'Позиция',
    ovr: 'OVR',
    selectUser: 'Выберите пользователя...',
    allUsers: 'Все пользователи',
    // AI Coach Panel
    aiCoachAnalysis: 'Анализ ИИ-тренера',
    poweredByGemini: 'На основе Gemini · Персонализировано под ваш профиль',
    analysisReady: 'Анализ готов',
    aiAnalyzing: 'ИИ-тренер анализирует ваш профиль...',
    aiAnalyzingDesc: 'Поиск скрытых закономерностей в вашей статистике · Gemini API',
    // AI Tabs
    tabStrengths: 'Сильные стороны',
    tabWeaknesses: 'Слабые стороны',
    tabTactics: 'Тактика',
    tabTraining: 'План тренировок',
    tabNutrition: 'Питание',
    tabInsights: 'ИИ-инсайты',
    // Strengths tab
    strengthsDesc: 'Ваши конкурентные преимущества — области, где вы превосходите игроков на вашей позиции.',
    keyStrengths: 'Ключевые сильные стороны',
    // Weaknesses tab
    weaknessesDesc: 'Области, требующие целенаправленного развития — честная оценка для перехода на следующий уровень рейтинга.',
    priorityImprovements: 'Приоритетные улучшения для повышения OVR',
    // Tactics tab
    tacticsDesc: 'Тактические советы для вашей позиции, адаптированные к вашему профилю статистики и стилю игры.',
    recommendedFormations: 'Рекомендуемые схемы для',
    // Training tab
    trainingDesc: '7-дневное расписание тренировок, персонализированное под ваши текущие показатели и целевые улучшения.',
    weeklyLoadSummary: 'Сводка недельной нагрузки',
    totalTraining: 'Всего тренировок',
    highIntensity: 'Высокая интенсивность',
    recoveryDays: 'Дни восстановления',
    // Nutrition tab
    nutritionDesc: 'Протокол питания, адаптированный к вашему типу диеты, физическим параметрам и тренировочной нагрузке.',
    dailyMacroTargets: 'Дневные цели по макросам (тренировочный день)',
    hydrationPriority: 'Приоритет гидратации',
    preMatchCarbs: 'Углеводы перед матчем',
    postSessionProtein: 'Белок после тренировки',
    sleepOptimization: 'Оптимизация сна',
    // Insights tab
    hiddenPatternDetection: 'Обнаружение скрытых закономерностей',
    insightsDesc: 'Gemini AI обнаружил неочевидные корреляции между вашими показателями — инсайты, которые человек-тренер может пропустить.',
    noPatternsTitle: 'Критических закономерностей не обнаружено',
    noPatternsDesc: 'Ваша статистика хорошо сбалансирована — скрытых рисков в вашем текущем профиле не найдено.',
    statCorrelationAnalysis: 'Анализ корреляции показателей',
    lateGameSprint: 'Эффективность спринта в конце игры',
    pressingVsBallCarrying: 'Вклад в прессинг vs ведение мяча',
    recoverySpeed: 'Скорость восстановления между сессиями',
    expectedGoals: 'Ожидаемый вклад в голы за матч',
    highRisk: 'высокий риск',
    mediumRisk: 'средний риск',
    lowRisk: 'низкий риск',
    // Intensity
    intensityLow: 'Низкая',
    intensityMedium: 'Средняя',
    intensityHigh: 'Высокая',
  },
  uz: {
    // Auth
    signIn: 'Kirish',
    createAccount: 'Hisob yaratish',
    welcomeBack: 'Xush kelibsiz',
    createYourProfile: 'Profilingizni yarating',
    signInDesc: 'Futbolchi kartasi va AI murabbiyga kirish uchun tizimga kiring',
    signUpDesc: 'Bugun Ultimate Team kartangizni yaratishni boshlang',
    emailAddress: 'Elektron pochta',
    password: 'Parol',
    fullName: 'To\'liq ism',
    confirmPassword: 'Parolni tasdiqlang',
    rememberMe: 'Meni eslab qol',
    forgotPassword: 'Parolni unutdingizmi?',
    signingIn: 'Kirish...',
    creatingAccount: 'Hisob yaratilmoqda...',
    agreeTerms: 'Men Foydalanish shartlari va Maxfiylik siyosatiga roziman',
    demoAccounts: 'Demo hisoblar',
    clickToFill: 'Avtomatik to\'ldirish uchun bosing',
    yourPlayerCard: 'Sizning futbolchi kartangiz.',
    yourLegacy: 'Sizning merosiz.',
    heroDesc: 'Haqiqiy ko\'rsatkichlar asosida Ultimate Team kartangizni yarating. Maksimal reytingga erishish uchun AI murabbiylik oling.',
    // Profile Setup
    physicalParameters: 'Jismoniy parametrlar',
    technicalSkills: 'Texnik ko\'nikmalar',
    positionStyle: 'Pozitsiya va o\'yin uslubi',
    physicalDesc: 'Jismoniy xususiyatlaringiz PHY, PAC va asosiy jismoniy ko\'rsatkichlarni belgilaydi',
    technicalDesc: 'Texnik qobiliyatlaringizni baholang — ular PAC, SHO, PAS, DRI, DEF ni hisoblaydi',
    positionDesc: 'Pozitsiya va uslubingiz statistika og\'irligiga va AI murabbiy tavsiyalariga ta\'sir qiladi',
    firstName: 'Ism',
    lastName: 'Familiya',
    age: 'Yosh',
    height: 'Bo\'y',
    weight: 'Vazn',
    stamina: 'Chidamlilik / Bardoshlilik',
    staminaDesc: 'Qancha vaqt yuqori samaradorlikni saqlay olasiz?',
    physicalStrength: 'Jismoniy kuch',
    strengthDesc: 'Yuqori tana kuchi, to\'pni himoya qilish, havo duellar',
    sleepHours: 'O\'rtacha uyqu (soat/kecha)',
    sleepDesc: 'Tiklanish sifati PHY reytingiga bevosita ta\'sir qiladi',
    nutritionRegime: 'Ovqatlanish rejimi',
    highProtein: 'Yuqori oqsilli',
    highProteinDesc: 'Mushak o\'sishi va tiklanish uchun optimallashtirilgan',
    balanced: 'Muvozanatli',
    balancedDesc: 'Standart makrolar, o\'rtacha tiklanish',
    lowCarb: 'Kam uglevod',
    lowCarbDesc: 'Uzoq muddatli sprint chidamliligini kamaytirishi mumkin',
    vegan: 'Vegan/O\'simlik asosida',
    veganDesc: 'Oqsil qo\'shimchalarini ehtiyotkorlik bilan tanlashni talab qiladi',
    lowStaminaWarning: 'Past chidamlilik aniqlandi — AI murabbiy 60 daqiqadan keyin samaradorlik pasayishini belgilaydi',
    // Slider labels
    needsWork: 'Ishlash kerak',
    average: 'O\'rtacha',
    good: 'Yaxshi',
    elite: 'Elite',
    fatiguesEarly: 'Tez charchaydi',
    full90: 'To\'liq 90 daqiqa',
    lightBuild: 'Yengil tuzilish',
    dominantAerial: 'Havoda ustun',
    // Position
    playingPosition: 'O\'yin pozitsiyasi',
    positionInfluence: 'Pozitsiya OVR hisobida statistika og\'irligiga ta\'sir qiladi',
    nationality: 'Millat',
    nationalityDesc: 'Futbolchi kartangizda ko\'rsatiladi',
    clubTeamName: 'Klub / Jamoa nomi',
    clubOptional: 'Ixtiyoriy — karta nishonida ko\'rsatiladi',
    preferredPlayingStyle: 'Afzal ko\'rilgan o\'yin uslubi',
    weeklyTrainingHours: 'Haftalik mashg\'ulot soatlari',
    weeklyTrainingDesc: 'Haftada tuzilgan futbol mashg\'ulotlari soatlari',
    profileSummary: 'Profil xulosasi',
    // Navigation
    backToLogin: 'Kirishga qaytish',
    previous: 'Orqaga',
    continue: 'Davom etish',
    generateMyCard: 'Kartamni yaratish',
    saving: 'Saqlanmoqda...',
    step: 'Qadam',
    of: 'dan',
    livePreview: 'Jonli ko\'rinish',
    // Dashboard
    playerDashboard: 'Futbolchi paneli',
    editProfile: 'Profilni tahrirlash',
    share: 'Ulashish',
    regenerateAI: 'AI ni yangilash',
    analyzing: 'Tahlil qilinmoqda...',
    // Admin
    adminDashboard: 'Administrator paneli',
    registeredUsers: 'Ro\'yxatdan o\'tgan foydalanuvchilar',
    sendNotification: 'Bildirishnoma yuborish',
    notificationTitle: 'Bildirishnoma sarlavhasi',
    notificationMessage: 'Xabar',
    sendToAll: 'Barcha foydalanuvchilarga yuborish',
    sendToUser: 'Foydalanuvchiga yuborish',
    sending: 'Yuborilmoqda...',
    notificationSent: 'Bildirishnoma yuborildi!',
    noUsers: 'Hali foydalanuvchilar ro\'yxatdan o\'tmagan.',
    userEmail: 'Email',
    userName: 'Ism',
    userRole: 'Rol',
    joinedAt: 'Qo\'shilgan sana',
    position: 'Pozitsiya',
    ovr: 'OVR',
    selectUser: 'Foydalanuvchini tanlang...',
    allUsers: 'Barcha foydalanuvchilar',
    // AI Coach Panel
    aiCoachAnalysis: 'AI Murabbiy Tahlili',
    poweredByGemini: 'Gemini tomonidan · Profilingizga moslashtirilgan',
    analysisReady: 'Tahlil tayyor',
    aiAnalyzing: 'AI murabbiy profilingizni tahlil qilmoqda...',
    aiAnalyzingDesc: 'Statistikangizda yashirin naqshlarni qidirmoqda · Gemini API',
    // AI Tabs
    tabStrengths: 'Kuchli tomonlar',
    tabWeaknesses: 'Zaif tomonlar',
    tabTactics: 'Taktika',
    tabTraining: 'Mashg\'ulot rejasi',
    tabNutrition: 'Ovqatlanish',
    tabInsights: 'AI tushunchalari',
    // Strengths tab
    strengthsDesc: 'Sizning raqobatdosh ustunliklaringiz — pozitsiyangizdagi o\'yinchilardan ustun bo\'lgan sohalar.',
    keyStrengths: 'Asosiy kuchli tomonlar',
    // Weaknesses tab
    weaknessesDesc: 'Maqsadli rivojlanishni talab qiladigan sohalar — keyingi reyting darajasiga o\'tish uchun halol baholash.',
    priorityImprovements: 'OVR ni oshirish uchun ustuvor yaxshilanishlar',
    // Tactics tab
    tacticsDesc: 'Statistika profilingiz va o\'yin uslubingizga moslashtirilgan pozitsiyaga xos taktik maslahatlar.',
    recommendedFormations: 'Tavsiya etilgan sxemalar',
    // Training tab
    trainingDesc: 'Joriy ko\'rsatkichlaringiz va maqsadli yaxshilanishlarga moslashtirilgan 7 kunlik mashg\'ulot jadvali.',
    weeklyLoadSummary: 'Haftalik yuk xulosasi',
    totalTraining: 'Jami mashg\'ulot',
    highIntensity: 'Yuqori intensivlik',
    recoveryDays: 'Tiklanish kunlari',
    // Nutrition tab
    nutritionDesc: 'Diet turingiz, jismoniy ko\'rsatkichlaringiz va mashg\'ulot yukiga moslashtirilgan ovqatlanish protokoli.',
    dailyMacroTargets: 'Kunlik makro maqsadlar (mashg\'ulot kuni)',
    hydrationPriority: 'Gidratatsiya ustuvorligi',
    preMatchCarbs: 'O\'yin oldidan uglevod',
    postSessionProtein: 'Mashg\'ulotdan keyin oqsil',
    sleepOptimization: 'Uyquni optimallashtirish',
    // Insights tab
    hiddenPatternDetection: 'Yashirin naqshlarni aniqlash',
    insightsDesc: 'Gemini AI statistikangiz o\'rtasidagi noaniq korrelyatsiyalarni topdi — inson murabbiy e\'tibor bermasligi mumkin bo\'lgan tushunchalar.',
    noPatternsTitle: 'Kritik naqshlar aniqlanmadi',
    noPatternsDesc: 'Statistikangiz yaxshi muvozanatlangan — joriy profilingizda yashirin xavflar topilmadi.',
    statCorrelationAnalysis: 'Statistika korrelyatsiya tahlili',
    lateGameSprint: 'O\'yin oxirida sprint samaradorligi',
    pressingVsBallCarrying: 'Pressing hissasi vs to\'p olib yurish',
    recoverySpeed: 'Sessiyalar orasida tiklanish tezligi',
    expectedGoals: 'O\'yin boshiga kutilgan gol hissasi',
    highRisk: 'yuqori xavf',
    mediumRisk: 'o\'rtacha xavf',
    lowRisk: 'past xavf',
    // Intensity
    intensityLow: 'Past',
    intensityMedium: 'O\'rtacha',
    intensityHigh: 'Yuqori',
  },
};

export type TranslationKey = keyof typeof translations.en;

export function getTranslations(language: Language): typeof translations.en {
  return translations[language] || translations.en;
}
